// Reading a visit note as text, without ever changing a character of it.
//
// Every function here works on the note's `body_text` exactly as the server
// stored it and returns OFFSETS into that string. Nothing rewrites, reflows or
// re-orders the note. That is the whole design constraint: a coding suggestion
// justifies itself with a verbatim quote, and the viewer has to be able to point
// at where that quote is. The moment this module "tidied" the text, the quote
// would land in the wrong place, and a biller would accept a code on the
// strength of the wrong sentence.
//
// (The app already has utils/formatProgressNote.js, which reflows an eCW note by
// inserting line breaks and dropping its page header. That is right for the
// appointments modal, which only displays; it is wrong here, because it does not
// preserve offsets and it only knows eCW. Notes here arrive from four EMRs.)
//
// No HTML from an EMR is rendered anywhere: these functions return plain strings
// and ranges, and NoteBody renders them as React text nodes.

// Section labels a note actually uses, across the EMRs Atlas reads. Used only to
// decide where a run-on note (eCW arrives as one unbroken line) may be split and
// which segments read as headings — never to decide what the note says.
const SECTION_LABELS = [
  "Chief complaint",
  "Chief complaints",
  "Subjective",
  "Subjective note",
  "Objective",
  "Objective note",
  "HPI",
  "History of present illness",
  "Review of systems",
  "ROS",
  "Past medical history",
  "Past psychiatric history",
  "Past surgical history",
  "Family history",
  "Social history",
  "Allergies",
  "Medications",
  "Current medications",
  "Vitals",
  "Vital signs",
  "Physical exam",
  "Mental status examination",
  "Risk assessment",
  "Assessment",
  "Assessment note",
  "Assessment and plan",
  "Impression",
  "Diagnosis",
  "Diagnoses",
  "Plan",
  "Plan note",
  "Plan of care",
  "Treatment plan",
  "Orders",
  "Goals",
  "Interventions",
  "Follow-up",
  "Procedures",
  "Superbill",
  "Patient",
  "Provider",
  "DOB",
  "Date",
  "Signed",
  "Signed by",
  "Electronic signature of Provider",
  "Sign off status",
];

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Case-fold without changing the string's LENGTH. `"\u0130".toLowerCase()` is two
// code points, so lowercasing a whole note shifts every index after such a
// character — and these functions return indexes into the original. Any
// character whose lower case is a different length keeps its original form: it
// then only matches itself, which is a missed match, never a shifted one.
function foldCase(value) {
  let out = "";
  for (const ch of value) {
    const lower = ch.toLowerCase();
    out += lower.length === ch.length ? lower : ch;
  }
  return out;
}

// "…anything but a letter… LABEL :". The leading group stands in for a lookbehind,
// which Safari did not support until 16.4 and Babel does not transpile.
const LABEL_BOUNDARY = new RegExp(
  `(^|[^A-Za-z])(${[...SECTION_LABELS]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join("|")})\\s*:`,
  "gi",
);

// A leading "Something:" on a segment. Bounded length so a sentence containing a
// colon does not become a heading.
const LEADING_LABEL = /^([A-Za-z][A-Za-z0-9 /&'()\-,.]{0,58}?)\s*:/;

const KNOWN = new Set(SECTION_LABELS.map((s) => s.toLowerCase()));

// Above this, the note is handed back as ONE segment. A megabyte of text splits
// into roughly fifty thousand of them, and fifty thousand paragraph elements
// re-rendered on every search keystroke is a frozen tab. One block loses the
// heading styling and nothing else: search, the quote highlight and copy all
// work on offsets and do not care how the text was divided.
const MAX_SEGMENTS = 4000;

function isAllCaps(value) {
  return value.length >= 3 && /[A-Z]/.test(value) && !/[a-z]/.test(value);
}

// The "Xxx:" a segment opens with, or null. Three ways to earn one: a label the
// EMRs are known to use, a segment that is nothing BUT a label, or a short
// title-cased label at the head of its own line.
function labelOf(text, atLineStart) {
  const match = LEADING_LABEL.exec(text);
  if (!match) return null;
  const label = match[1].trim();
  if (!label) return null;
  if (KNOWN.has(label.toLowerCase())) return `${label}:`;
  if (text.length === match[0].length && label.length <= 48) return `${label}:`;
  // An unfamiliar label earns a heading only on its own line and only if it
  // reads like a label: a mid-sentence colon ("the patient reports the
  // following: nausea") must not become one, because assessmentIndex searches
  // headings and would then point at a sentence.
  if (
    atLineStart &&
    label.length <= 40 &&
    label.split(/\s+/).length <= 3 &&
    /^[A-Z]/.test(label) &&
    !label.includes(".")
  ) {
    return `${label}:`;
  }
  return null;
}

/**
 * Split a note into displayable segments without altering it.
 *
 * Returns [{start, end, text, label, heading}], where `text` is always
 * `body_text.slice(start, end)`. Boundaries fall on line breaks and immediately
 * before a known section label, so a note that arrives as one unbroken line
 * still reads as sections. Only whitespace is ever dropped.
 */
export function segmentNote(body) {
  if (!body || typeof body !== "string") return [];

  const bounds = new Set([0, body.length]);
  for (let i = 0; i < body.length; i += 1) {
    if (body[i] === "\n") {
      bounds.add(i);
      bounds.add(i + 1);
    }
  }
  LABEL_BOUNDARY.lastIndex = 0;
  let match = LABEL_BOUNDARY.exec(body);
  while (match) {
    bounds.add(match.index + match[1].length);
    // Past the whole label, stopping one character short so the colon is still
    // available as the non-letter prefix a back-to-back label needs. Stepping
    // only one character in would re-find the shorter label nested inside a
    // longer one — "Provider:" inside "Electronic signature of Provider:" —
    // and split a heading down the middle.
    LABEL_BOUNDARY.lastIndex = match.index + match[0].length - 1;
    match = LABEL_BOUNDARY.exec(body);
  }

  const ordered = [...bounds].sort((a, b) => a - b);
  const segments = [];
  for (let i = 0; i < ordered.length - 1; i += 1) {
    let start = ordered[i];
    let end = ordered[i + 1];
    while (start < end && /\s/.test(body[start])) start += 1;
    while (end > start && /\s/.test(body[end - 1])) end -= 1;
    if (end <= start) continue;
    const text = body.slice(start, end);
    const label = labelOf(text, start === 0 || body[start - 1] === "\n");
    segments.push({
      start,
      end,
      text,
      label,
      heading: !!label || isAllCaps(text),
    });
  }
  if (segments.length > MAX_SEGMENTS) {
    const start = segments[0].start;
    const end = segments[segments.length - 1].end;
    return [{ start, end, text: body.slice(start, end), label: null, heading: false }];
  }
  return segments;
}

/** Every case-insensitive occurrence of a literal query, as ranges. */
export function findMatches(body, query) {
  if (!body || typeof body !== "string") return [];
  if (!query || typeof query !== "string" || !query.trim()) return [];
  const needle = foldCase(query);
  const haystack = foldCase(body);
  const ranges = [];
  let from = haystack.indexOf(needle);
  while (from !== -1) {
    ranges.push({ start: from, end: from + needle.length });
    from = haystack.indexOf(needle, from + needle.length);
  }
  return ranges;
}

// The note with every run of whitespace collapsed to one space, plus a map from
// each normalised character back to its index in the original.
function normalizeWithMap(body) {
  let out = "";
  const map = [];
  let prevSpace = true; // leading whitespace is dropped
  for (let i = 0; i < body.length; i += 1) {
    const ch = body[i];
    if (/\s/.test(ch)) {
      if (prevSpace) continue;
      out += " ";
      map.push(i);
      prevSpace = true;
    } else {
      out += ch;
      map.push(i);
      prevSpace = false;
    }
  }
  while (out.endsWith(" ")) {
    out = out.slice(0, -1);
    map.pop();
  }
  return { text: out, map };
}

/**
 * Where a suggestion's quote sits in the note, as a range, or null.
 *
 * Whitespace-insensitive on purpose: a note stored with line breaks inside a
 * sentence still has to match the quote the model returned on one line. Nothing
 * else is normalised — a quote that differs by a word does not match, which is
 * what drops the suggestion line rather than highlighting the wrong sentence.
 */
export function findQuote(body, quote) {
  if (!body || typeof body !== "string") return null;
  if (!quote || typeof quote !== "string" || !quote.trim()) return null;
  const note = normalizeWithMap(body);
  const needle = foldCase(normalizeWithMap(quote).text);
  if (!needle) return null;
  const at = foldCase(note.text).indexOf(needle);
  if (at === -1) return null;
  return { start: note.map[at], end: note.map[at + needle.length - 1] + 1 };
}

/** Index of the segment that opens the assessment, or -1. */
export function assessmentIndex(segments) {
  if (!Array.isArray(segments)) return -1;
  return segments.findIndex(
    (s) => s.heading && /^assessment/i.test(s.label || s.text),
  );
}
